import { useCallback, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { useApiCall } from '@/hooks/useApiCall';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { ToolAliasApiClient } from '@/features/traceability/api/ToolAliasApiClient';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import type { ToolAliasesResponse } from '@/features/traceability/api/ToolAliasApiClient.types';
import type { Tool } from '@/features/traceability/type/Tool';
import { formatDate } from '@/features/toolView/utils/toolViewHelpers';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { groupToolsByReference, type ToolReferenceGroup } from '../utils/groupToolsByReference';
import {
  groupActivityDates,
  toolActivityDates,
  type ActivityDates,
} from '../utils/toolActivityDates';

type DeleteTarget =
  | { kind: 'tool'; toolId: string }
  | { kind: 'reference'; reference: string; toolCount: number; alias?: string };

const PENDING = new Promise<never>(() => {});

/** Admin-only tab: delete tools or whole references (tools + traces + alias). */
export function ToolDeletionTab() {
  const { t, language } = useTranslation();
  const http = useApiClient();
  const client = useMemo(() => (http ? new TraceabilityApiClient(http) : null), [http]);
  const aliasClient = useMemo(() => (http ? new ToolAliasApiClient(http) : null), [http]);

  const [target, setTarget] = useState<DeleteTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    data: toolsData,
    error: loadError,
    isLoading,
    retry,
  } = useApiCall<AllToolsResponse>({
    apiFn: (signal) => (client ? client.toolReadAll(undefined, { signal }) : PENDING),
    deps: [client],
  });

  const { data: aliasData, retry: retryAliases } = useApiCall<ToolAliasesResponse>({
    apiFn: (signal) => (aliasClient ? aliasClient.aliasReadAll(undefined, { signal }) : PENDING),
    deps: [aliasClient],
  });

  // Complete union listing: tool references AND alias rows (alias-only refs included).
  const grouped = useMemo(
    () => groupToolsByReference(toolsData?.tools ?? [], aliasData?.toolAliases ?? []),
    [toolsData, aliasData]
  );
  const toolCount = grouped.referenced.reduce((n, g) => n + g.tools.length, 0);

  const confirmDelete = useCallback(async () => {
    if (!client || !target) return;
    setBusy(true);
    setActionError(null);
    const result =
      target.kind === 'tool'
        ? await client.toolDelete({ toolId: target.toolId })
        : await client.toolDeleteByReference({ reference: target.reference });
    setBusy(false);
    if (!result.ok) {
      setActionError(result.message ?? t('adminTools.error.generic'));
      return;
    }
    setTarget(null);
    retry();
    retryAliases();
  }, [client, target, retry, retryAliases, t]);

  const datesLabel = (dates: ActivityDates) =>
    dates.createdAt === null && dates.updatedAt === null
      ? t('adminTools.dates.none')
      : t('adminTools.dates.line', {
          created: formatDate(dates.createdAt, language, t, 'adminTools.dates.none'),
          updated: formatDate(dates.updatedAt, language, t, 'adminTools.dates.none'),
        });

  // An alias-only group has no tools to derive from — the alias row's own timestamps apply.
  const groupDates = (group: ToolReferenceGroup): ActivityDates => {
    if (group.tools.length > 0) return groupActivityDates(group.tools);
    return {
      createdAt: group.alias?.createdAt ?? null,
      updatedAt: group.alias?.updatedAt ?? null,
    };
  };

  const toolRow = (tool: Tool) => (
    <li key={tool.toolId} className="flex items-center justify-between px-5 py-2.5">
      <span className="flex items-baseline gap-3.5">
        <span className="font-mono text-sm text-foreground">{tool.toolId}</span>
        <span className="text-xs text-muted-foreground">
          {datesLabel(toolActivityDates(tool))}
        </span>
      </span>
      <Button
        variant="ghost-destructive"
        size="sm"
        onClick={() => setTarget({ kind: 'tool', toolId: tool.toolId })}
      >
        <Trash2 aria-hidden />
        {t('adminTools.actions.deleteTool')}
      </Button>
    </li>
  );

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">{t('adminTools.subtitle')}</p>

      {loadError !== null && (
        <Callout
          tone="error"
          action={
            <Button variant="outline" size="sm" onClick={retry}>
              {t('adminTools.actions.retry')}
            </Button>
          }
        >
          {loadError}
        </Callout>
      )}
      {actionError !== null && <Callout tone="error">{actionError}</Callout>}
      {isLoading && (
        <Callout tone="neutral" loading>
          {t('adminTools.loading')}
        </Callout>
      )}

      {!isLoading && loadError === null && (
        <>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{t('adminTools.summary.references', { count: grouped.referenced.length })}</span>
            <span>{t('adminTools.summary.tools', { count: toolCount })}</span>
            <span>
              {t('adminTools.summary.unreferenced', { count: grouped.unreferenced.length })}
            </span>
          </div>

          {grouped.referenced.map((group) => (
            <Card as="section" key={group.reference} padding="none" className="overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-5 py-3.5">
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-mono text-[15px] font-semibold text-foreground">
                    {group.reference}
                  </span>
                  <Badge>{t('adminTools.group.count', { count: group.tools.length })}</Badge>
                  {group.alias && (
                    <span className="text-xs text-muted-foreground">
                      {t('adminTools.group.alias', { alias: group.alias.alias })}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {datesLabel(groupDates(group))}
                  </span>
                </div>
                <Button
                  variant="ghost-destructive"
                  size="sm"
                  onClick={() =>
                    setTarget({
                      kind: 'reference',
                      reference: group.reference,
                      toolCount: group.tools.length,
                      alias: group.alias?.alias,
                    })
                  }
                >
                  {t('adminTools.actions.deleteReference')}
                </Button>
              </div>
              {group.tools.length > 0 && (
                <ul className="divide-y border-t">{group.tools.map(toolRow)}</ul>
              )}
            </Card>
          ))}

          {grouped.unreferenced.length > 0 && (
            <>
              <p className="-mb-3 px-1 text-sm text-muted-foreground">
                {t('adminTools.unreferenced.title')}
              </p>
              <Card as="section" padding="none" className="overflow-hidden">
                <ul className="divide-y">{grouped.unreferenced.map(toolRow)}</ul>
              </Card>
            </>
          )}
        </>
      )}

      <ConfirmDialog
        open={target !== null}
        busy={busy}
        title={
          target?.kind === 'reference'
            ? t('adminTools.dialog.reference.title', { reference: target.reference })
            : t('adminTools.dialog.tool.title', { toolId: target?.toolId ?? '' })
        }
        description={
          target?.kind === 'reference'
            ? target.toolCount === 0
              ? t('adminTools.dialog.reference.descriptionAliasOnly', {
                  reference: target.reference,
                })
              : t('adminTools.dialog.reference.description', {
                  count: target.toolCount,
                  reference: target.reference,
                  alias: target.alias ?? '',
                })
            : t('adminTools.dialog.tool.description', { toolId: target?.toolId ?? '' })
        }
        confirmLabel={
          target?.kind === 'reference'
            ? t('adminTools.dialog.reference.confirm')
            : t('adminTools.dialog.tool.confirm')
        }
        cancelLabel={t('adminTools.dialog.cancel')}
        onConfirm={confirmDelete}
        onCancel={() => {
          setTarget(null);
          setActionError(null);
        }}
      />
    </div>
  );
}
